import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, Hash, HelpCircle, Pin } from 'lucide-react';
import { API } from '@/App';

export default function CommunitiesPage({ unreadCommunities = {} }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await fetch(`${API}/communities`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setGroups(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>
          Communities
        </h1>
        <p className="text-sm text-gray-600">Join course discussions</p>
      </div>

      {/* Communities List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200/50">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <h3 className="text-base font-semibold text-gray-700 mb-1">No communities</h3>
          <p className="text-xs text-gray-500">Communities will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((group) => {
            const unreadCount = unreadCommunities[group.community_id] || 0;
            const isPinned = group.pinned;
            return (
              <div
                key={group.community_id}
                data-testid={`community-card-${group.community_id}`}
                onClick={() => navigate(`/community/${group.community_id}`)}
                className={`bg-gray-50 rounded-xl p-3 border transition-all cursor-pointer ${
                  unreadCount > 0 
                    ? 'border-indigo-300 bg-indigo-50/50' 
                    : isPinned 
                      ? 'border-amber-200 bg-amber-50/30'
                      : 'border-gray-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPinned ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                      <Hash className={`w-5 h-5 ${isPinned ? 'text-amber-600' : 'text-indigo-600'}`} />
                    </div>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-gray-900 truncate">{group.name}</h3>
                      {isPinned && <Pin className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />}
                      {unreadCount > 0 && <span className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] font-semibold rounded-full shrink-0">New</span>}
                    </div>
                    <p className="text-xs text-gray-600 truncate">{group.description}</p>
                  </div>
                  <Button
                    data-testid={`open-chat-btn-${group.community_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/community/${group.community_id}`);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 h-8 text-xs font-semibold shrink-0 hidden sm:flex"
                  >
                    Open
                  </Button>
                </div>
              </div>
            );
          })}
          
          {/* Can't find your course */}
          <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200/50">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <HelpCircle className="w-4 h-4 text-indigo-600 shrink-0" />
                <span className="text-xs text-gray-700 truncate">Can't find your course?</span>
              </div>
              <Button
                onClick={() => navigate('/suggestions')}
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-lg px-3 h-7 text-xs shrink-0"
              >
                Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
